import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ActionItem } from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteActions } from '@next/core-lib/site';
import { Store, select } from '@ngrx/store';

import { combineLatest, of, Observable, ReplaySubject } from 'rxjs';

import { map, takeUntil } from 'rxjs/operators';

import {
  SiteState,
  Treatment,
  TreatmentSchema,
  AidAdvice,
  SalesOrderPeriod,
  TreatmentWithActions,
  Template,
  ProvinceEnum,
} from '../shared/models';
import {
  GetEditingTemplate,
  AddTreatment,
  EditTreatment,
  RemoveTreatment,
  SaveTreatment,
  AddTreatmentSchema,
  EditTreatmentSchema,
  RemoveTreatmentSchema,
  OpenCopyTreatmentsDialog,
  SetEditingTemplate,
} from './store/actions';
import {
  selectEditingTemplate,
  selectEditingTreatments,
  selectEditingTreatmentSchemas,
  areAidAdvicesValid,
  areTreatmentsValid,
  selectTreatmentNamesAndDosages,
} from './store/selectors';
import { ApiService } from '../shared/services/api.service';
import { selectSoilTypes } from '../soil-type/store/soil-type.selectors';
import { selectSalesOrderPeriods, selectCrops } from '../store/selectors';

enum ButtonAction {
  copy = 'copy',
  add = 'add',
}

interface ButtonItem {
  icon: string;
  viewValue: string;
  key: ButtonAction;
}

enum TreatmentAction {
  copy = 'copy',
  edit = 'edit',
  remove = 'remove',
  preventRemoveTreatmentUsedInSchema = 'preventRemoveTreatmentUsedInSchema',
}

enum SchemaAction {
  edit = 'edit',
  remove = 'remove',
}

@Component({
  selector: 'app-template',
  templateUrl: './template.component.html',
  styleUrls: ['./template.component.scss'],
  standalone: false,
})
export class TemplateComponent implements OnInit, OnDestroy {
  private readonly locale = inject(LocaleService);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly route = inject(ActivatedRoute);
  protected readonly router = inject(Router);
  private readonly dialog = inject(DialogService);
  private readonly serviceApi = inject(ApiService);

  title = this.locale.translate('template');
  template$ = this.store.pipe(select(selectEditingTemplate));
  template: Template;
  treatments$ = this.store.pipe(select(selectEditingTreatments));
  areTreatmentsValid$ = this.store.pipe(select(areTreatmentsValid));
  treatmentSchemas$ = this.store.pipe(select(selectEditingTreatmentSchemas));
  salesOrderPeriods$ = this.store.pipe(select(selectSalesOrderPeriods));
  buttonItems: ButtonItem[] = [];
  selectedCheckboxId: string | null;
  provinces: ProvinceEnum[];

  displayCropName$ = combineLatest([this.template$, this.store.select(selectCrops)]).pipe(
    map(([template, crops]) => {
      if (!template) {
        return '';
      }
      return crops.find((crop) => crop.id === template.cropId)?.name;
    }),
  );

  displaySoilType$ = combineLatest([this.template$, this.store.select(selectSoilTypes)]).pipe(
    map(([template, soilTypes]) => {
      if (!template) {
        return '';
      }

      if (template.soilTypeId === null) {
        return this.locale.translate('soilTypeIndependent');
      }
      return soilTypes.find((soilType) => soilType.id === template.soilTypeId)?.name;
    }),
  );

  treatmentsWithActions$ = combineLatest([
    this.areTreatmentsValid$,
    this.treatments$,
    this.store.select(selectEditingTreatmentSchemas),
  ]).pipe(
    map(([hasValidTreatments, treatments, treatmentSchemas]) => {
      if (!treatments) {
        return;
      }
      let treatmentIdsInSchema: string[] = [];
      treatmentSchemas.forEach((schema) => {
        treatmentIdsInSchema = [...treatmentIdsInSchema, ...schema.treatmentIds];
      });
      let lastPeriod: number;
      return treatments.map((treatment: Treatment) => {
        const treatmentsWithActions = {
          ...treatment,
          hasDate: treatment.periodId !== lastPeriod,
          actions: [
            {
              icon: 'file_copy',
              viewValue: this.locale.translate('copy'),
              key: TreatmentAction.copy,
              disabled: !hasValidTreatments,
            },
            {
              icon: 'edit',
              viewValue: this.locale.translate('edit'),
              key: TreatmentAction.edit,
              disabled: !hasValidTreatments && this.isTreatmentValid(treatment),
            },
            {
              icon: treatmentIdsInSchema.indexOf(treatment.id) !== -1 ? 'delete_forever' : 'delete',
              viewValue: this.locale.translate('remove'),
              key:
                treatmentIdsInSchema.indexOf(treatment.id) !== -1
                  ? TreatmentAction.preventRemoveTreatmentUsedInSchema
                  : TreatmentAction.remove,
              disabled: !hasValidTreatments && this.isTreatmentValid(treatment),
            },
          ],
        } as TreatmentWithActions;
        lastPeriod = treatment.periodId;
        return treatmentsWithActions;
      });
    }),
  );

  treatmentSchemaActions$: Observable<ActionItem[]> = of([]);

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  ngOnInit() {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: this.title }));
    const id = +this.route.snapshot.paramMap.get('id');
    this.store.dispatch(new GetEditingTemplate(id));

    this.buttonItems = [
      {
        icon: 'file_copy',
        viewValue: this.locale.translate('copyStandard'),
        key: ButtonAction.copy,
      },
      {
        icon: 'add',
        viewValue: this.locale.translate('addTreatmentSchema'),
        key: ButtonAction.add,
      },
    ];

    this.areTreatmentsValid$.subscribe((hasValidTreatments) => {
      this.treatmentSchemaActions$ = of([
        {
          icon: 'edit',
          viewValue: this.locale.translate('edit'),
          key: SchemaAction.edit,
          disabled: !hasValidTreatments,
        },
        {
          icon: 'delete',
          viewValue: this.locale.translate('remove'),
          key: SchemaAction.remove,
          disabled: !hasValidTreatments,
        },
      ]);
    });

    this.template$.subscribe((t) => {
      this.template = t;
      this.selectedCheckboxId = this.template?.defaultTreatmentSchemaId ?? null;
      if (this.template?.province) {
        this.provinces = this.template.province;
      }
    });
  }

  buttonAction(data: string) {
    switch (data) {
      case ButtonAction.copy:
        this.copyTreatments();
        break;
      case ButtonAction.add:
        this.addTreatmentSchema();
        break;
    }
  }

  copyTreatments() {
    this.store.dispatch(new OpenCopyTreatmentsDialog());
  }

  treatmentAction(key: string, treatmentWithActions: TreatmentWithActions, index: number) {
    const { actions, hasDate, ...treatment } = treatmentWithActions;
    switch (key) {
      case TreatmentAction.copy:
        this.copyTreatment(treatment);
        break;
      case TreatmentAction.edit:
        this.editTreatment(treatment, index);
        break;
      case TreatmentAction.remove:
        this.removeTreatment(treatment);
        break;
      case TreatmentAction.preventRemoveTreatmentUsedInSchema:
        this.dialog.notify({
          title: this.locale.translate('important'),
          text: this.locale.translate('cantRemoveTreatmentUsedInSchema'),
        });
        break;
    }
  }

  addTreatment() {
    this.store.dispatch(new AddTreatment());
  }

  copyTreatment(treatment: Treatment) {
    const newTreatment: Treatment = {
      ...treatment,
      id: undefined,
    };
    this.salesOrderPeriods$.subscribe((salesOrderPeriods) => {
      this.store.dispatch(new SaveTreatment(newTreatment, -1, salesOrderPeriods));
    });
  }

  editTreatment(treatment: Treatment, index: number) {
    this.store.dispatch(new EditTreatment(treatment, index));
  }

  removeTreatment(treatment: Treatment) {
    this.store.dispatch(new RemoveTreatment(treatment));
  }

  treatmentSchemaAction(key: string, treatmentSchema: TreatmentSchema) {
    switch (key) {
      case SchemaAction.edit:
        this.editTreatmentSchema(treatmentSchema);
        break;
      case SchemaAction.remove:
        this.removeTreatmentSchema(treatmentSchema);
        break;
    }
  }

  addTreatmentSchema() {
    this.store.dispatch(new AddTreatmentSchema());
  }

  editTreatmentSchema(treatmentSchema: TreatmentSchema) {
    this.store.dispatch(new EditTreatmentSchema(treatmentSchema));
  }

  removeTreatmentSchema(treatmentSchema: TreatmentSchema) {
    if (this.template?.defaultTreatmentSchemaId === treatmentSchema.id) {
      this.serviceApi
        .updateDefaultTreatmentSchemaIdForTemplate(this.template.id, {
          treatmentSchemaId: null,
        })
        .pipe(takeUntil(this.destroyed$))
        .subscribe((_) => this.store.dispatch(new RemoveTreatmentSchema(treatmentSchema)));
    } else {
      this.store.dispatch(new RemoveTreatmentSchema(treatmentSchema));
    }
  }

  areAidAdvicesValid(aidAdvices: AidAdvice[]) {
    return this.store.pipe(select(areAidAdvicesValid, aidAdvices));
  }

  getSalesOrderPeriod(salesOrderPeriods: SalesOrderPeriod[], periodId: number) {
    return salesOrderPeriods.find((salesOrderPeriod) => salesOrderPeriod.id === periodId);
  }

  getTreatmentNamesAndDosages$(treatment: Treatment) {
    return this.store.pipe(select(selectTreatmentNamesAndDosages, treatment));
  }

  openOverview() {
    this.router.navigate([`/`], { queryParamsHandling: 'preserve' });
  }

  isTreatmentValid = (treatment: Treatment): boolean =>
    !treatment.isCorrupt && (!this.template.isPlanetProof || !treatment.forbiddenForPlanetProof);

  setSchemaAsDefault(checked: boolean, treatmentSchema: TreatmentSchema) {
    const defaultTreatmentSchemaId = checked ? treatmentSchema.id : null;
    this.selectedCheckboxId = defaultTreatmentSchemaId;

    if (checked) {
      this.serviceApi
        .updateDefaultTreatmentSchemaIdForTemplate(this.template.id, {
          treatmentSchemaId: treatmentSchema.id,
        })
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() =>
          this.store.dispatch(
            new SetEditingTemplate({
              ...this.template,
              defaultTreatmentSchemaId,
            }),
          ),
        );
    } else {
      this.serviceApi
        .updateDefaultTreatmentSchemaIdForTemplate(this.template.id, {
          treatmentSchemaId: null,
        })
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() =>
          this.store.dispatch(
            new SetEditingTemplate({
              ...this.template,
              defaultTreatmentSchemaId,
            }),
          ),
        );
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
